import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, apiErrorMessage } from "../api/client";
import type { EnrichmentBatch, EnrichmentMember } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { PAGE_SIZE, useListParams } from "../lib/useListParams";
import { ListShell, SortHeader } from "../components/ListShell";
import { useCsvColumns } from "../lib/columnChoice";
import { downloadNarrowed } from "../lib/csvDownload";
import { orAbsent, shortDate } from "../lib/format";
import { batchWords, useCampaignLabels } from "../lib/campaignLabels";
import { Spinner } from "../components/Spinner";
import { EnrichmentRequests } from "../components/EnrichmentRequests";
import { EnrichmentComingUp } from "../components/EnrichmentComingUp";
import { expectedWords } from "../lib/enrichment";

/* Watching a batch of enrichment go through.
 *
 * Sending five hundred records off is one press. What happens next takes hours,
 * and until now there was no way to look at it.
 *
 * The five states are kept apart on purpose and the copy below says what each
 * one means, because the difference between two of them has already cost this
 * system real work: on 8 September twenty-five records were read as "nobody
 * there" when the real cause was an exhausted ZoomInfo balance. "We asked and
 * nothing came back" and "we could not ask" look identical in a single number
 * and mean opposite things. */
const FILTERS = ["batch", "outcome"] as const;

const CSV_DEFAULT = ["label", "entity_type", "outcome", "looked_at",
                     "employment_since_lookup"] as const;

/* The engine's own definitions, from docs/web-enrichment.md, not this screen's
   reading of them. The recheck windows are lake.lookup_policy's. */
/* signals and employment come back as jsonb, so they arrive as unknown and are
   read defensively. A shape that is not what these expect returns nothing,
   rather than throwing inside a table cell and taking the page with it. */
function pagesRead(v: unknown): number {
  if (!v || typeof v !== "object" || Array.isArray(v)) return 0;
  return Object.values(v as Record<string, unknown>)
    .reduce<number>((n, x) => n + (typeof x === "number" ? x : 0), 0);
}

type Job = { title?: unknown; company?: unknown; person?: unknown; standing?: unknown };

function jobsFound(v: unknown): Job[] {
  return Array.isArray(v) ? (v as Job[]).filter((j) => j && typeof j === "object") : [];
}

function jobLine(j: Job): string {
  const title = typeof j.title === "string" && j.title ? j.title : null;
  const company = typeof j.company === "string" && j.company ? j.company : null;
  const person = typeof j.person === "string" && j.person ? j.person : null;
  // On a company row the company is the subject and the person is the news, so
  // lake.lookup_batch_people sends person where a person row gets company.
  if (person) return title ? `${person}, ${title}` : person;
  // A title with no employer and an employer with no title are both real
  // answers, and neither should render as "null at null".
  if (title && company) return `${title} at ${company}`;
  return title ?? company ?? "a job with no title and no employer";
}

/* What to say when a lookup found pages and this column has nothing to show.
 *
 * Only a person has a job to be missing. Saying "no job on them" about a
 * building is a claim about the wrong kind of thing, and it was on screen for
 * 1,880 properties and all 204 found companies, because the view filled
 * employment for people only until engine migration 068.
 *
 * A property gets nothing here on purpose. What a property lookup writes is a
 * unit count, parking and chargers, which this screen does not carry, and the
 * outcome column already tells the reader what found means. An empty cell
 * beside "found" is honest. A sentence inventing an absence is not. */
function nothingToShow(entityType: string | null | undefined): string | null {
  if (entityType === "person") return "pages, but no job on them";
  if (entityType === "company") return "pages, but no names on them";
  return null;
}

const OUTCOMES = [
  {
    key: "found", label: "Found",
    means: "A page named them. What it said is on the record.",
    again: "Asked again after 180 days.",
  },
  {
    key: "no_result", label: "Nothing found",
    means: "Pages answered, and none of them named this person or company.",
    again: "Asked again after 90 days.",
  },
  {
    key: "unreachable", label: "Could not reach",
    means: "No site answered. It was down, timed out, or refused us.",
    again: "Asked again after 7 days.",
  },
] as const;

export function Enrichment({ session }: { session: SignedIn }) {
  const params = useListParams("looked_at", FILTERS);
  const labels = useCampaignLabels(session.idToken);
  const csvColumns = useCsvColumns("enrichment-members", "enrichment_member", CSV_DEFAULT);
  const queryClient = useQueryClient();
  const batch = params.filters["batch"] ?? null;

  const batches = useQuery({
    queryKey: ["enrichment-batches"],
    // A batch moves while somebody watches it, which is the whole point of the
    // screen, so this one refetches on its own. Nothing reorders: the list is
    // ordered by when each batch started.
    refetchInterval: 30000,
    queryFn: async () => {
      const { data, error } = await apiClient(session.idToken)
        .from("enrichment_batch")
        .select("batch,members,waiting,found,no_result,unreachable,never_queued,signals,signals_open,first_looked_at,last_looked_at,done,expected_done_at")
        .order("first_looked_at", { ascending: false, nullsFirst: false })
        .limit(50);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as EnrichmentBatch[];
    },
  });

  const narrowed = (opts?: { count?: "exact"; head?: boolean }, columns?: string) => {
    let q = apiClient(session.idToken)
      .from("enrichment_member")
      .select(columns ?? "batch,entity_type,entity_id,label,outcome,looked_at,signals,employment,employment_since_lookup", opts);
    if (batch) q = q.eq("batch", batch);
    if (params.filters["outcome"] === "waiting") q = q.is("outcome", null);
    else if (params.filters["outcome"]) q = q.eq("outcome", params.filters["outcome"]);
    if (params.search.trim()) q = q.ilike("label", `%${params.search.trim()}%`);
    return q;
  };

  const members = useQuery({
    queryKey: ["enrichment-members", batch, params.page, params.sort, params.ascending, params.filters, params.search],
    enabled: Boolean(batch),
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      const { data, error } = await narrowed()
        .order(params.sort, { ascending: params.ascending, nullsFirst: false })
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as EnrichmentMember[];
    },
  });

  const memberCount = useQuery({
    queryKey: ["enrichment-members", "count", batch, params.filters, params.search],
    enabled: Boolean(batch),
    queryFn: async () => {
      const { error, count } = await narrowed({ count: "exact", head: true });
      if (error) throw new Error(apiErrorMessage(error));
      return count ?? null;
    },
  });

  const chosen = batches.data?.find((b) => b.batch === batch) ?? null;

  return (
    <>
      <main>
        <h1>Enrichment</h1>
        <p className="muted">
          The campaigns somebody asked to have worked by hand, the planned ones coming, and the records in web enrichment.
        </p>

        <EnrichmentRequests token={session.idToken} />

        <EnrichmentComingUp token={session.idToken} />

        <h2>Web enrichment</h2>
        {batches.isLoading ? <p className="muted"><Spinner />Loading.</p>
          : batches.isError ? <p className="refusal">{(batches.error as Error).message}</p>
          : !batches.data?.length ? (
            <p className="muted">
              No batches yet. "Run web enrichment" on a campaign's
              page starts one.
            </p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Batch</th>
                  <th className="num">Records</th>
                  <th className="num">Waiting</th>
                  <th className="num">Found</th>
                  <th className="num">Nothing found</th>
                  <th className="num">Could not reach</th>
                  <th className="num">Not sent</th>
                  <th>Last worked</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {batches.data.map((b) => (
                  <tr
                    key={b.batch}
                    className="clickable"
                    onClick={() => params.set({ filters: { ...params.filters, batch: b.batch ?? "" }, page: 1 })}
                  >
                    <td>
                      <button className="link" onClick={(e) => {
                        e.stopPropagation();
                        params.set({ filters: { ...params.filters, batch: b.batch ?? "" }, page: 1 });
                      }}>{orAbsent(batchWords(b.batch, labels.data))}</button>
                    </td>
                    <td className="num">{b.members ?? 0}</td>
                    <td className="num">{b.waiting ?? 0}</td>
                    <td className="num">{b.found ?? 0}</td>
                    <td className="num">{b.no_result ?? 0}</td>
                    {/* Red, because this is the count that means we did not get
                        to ask rather than that nobody was there. */}
                    <td className="num">
                      {b.unreachable ? <span className="refusal">{b.unreachable}</span> : 0}
                    </td>
                    <td className="num">{b.never_queued ?? 0}</td>
                    <td>{shortDate(b.last_looked_at)}</td>
                    <td className="muted">
                      {b.done ? "finished"
                        : b.waiting ? `working, ${expectedWords(b.expected_done_at)}`
                        : b.signals_open ? `reading ${b.signals_open.toLocaleString()} pages`
                        : "working"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

        <h2>What the outcomes mean</h2>
        <dl className="meanings">
          {OUTCOMES.map((o) => (
            <div key={o.key}>
              <dt>{o.label}</dt>
              <dd>{o.means} <span className="muted">{o.again}</span></dd>
            </div>
          ))}
          <div>
            <dt>Not sent</dt>
            <dd>
              In the batch, not searched. We checked recently.
            </dd>
          </div>
          <div>
            <dt>Waiting</dt>
            <dd>Sent, and no answer back yet.</dd>
          </div>
        </dl>
      </main>

      {batch ? (
        <ListShell
          title={chosen ? `${batch}` : batch}
          params={params}
          csvColumns={csvColumns}
          total={memberCount.data ?? null}
          totalPending={memberCount.isPending}
          rowCount={members.data?.length ?? 0}
          isLoading={members.isLoading}
          isFetching={members.isFetching}
          isError={members.isError}
          error={members.error as Error | null}
          dataUpdatedAt={members.dataUpdatedAt}
          onRefresh={() => queryClient.invalidateQueries({ queryKey: ["enrichment-members"] })}
          onRetry={() => members.refetch()}
          onDownload={() =>
            downloadNarrowed({
              name: `enrichment-${batch}`,
              columns: csvColumns.chosen,
              total: memberCount.data ?? null,
              fetchAll: async (ceiling) => {
                const { data, error } = await narrowed(undefined, csvColumns.chosen.join(","))
                  .order(params.sort, { ascending: params.ascending, nullsFirst: false })
                  .range(0, ceiling - 1);
                if (error) throw new Error(apiErrorMessage(error));
                return (data ?? []) as unknown as EnrichmentMember[];
              },
            })
          }
          searchLabel="Search by name"
          emptyEverything="No rows match. Clear the outcome filter to see all."
          filters={
            <>
              <label htmlFor="f-outcome">Outcome</label>
              <select
                id="f-outcome" value={params.filters["outcome"] ?? ""}
                onChange={(e) => params.set({ filters: { ...params.filters, outcome: e.target.value } })}
              >
                <option value="">any</option>
                <option value="waiting">waiting</option>
                {OUTCOMES.map((o) => <option key={o.key} value={o.key}>{o.label.toLowerCase()}</option>)}
              </select>
              <button onClick={() => params.set({ filters: { ...params.filters, batch: "" }, page: 1 })}>
                Close this batch
              </button>
            </>
          }
        >
          <table>
            <thead>
              <tr>
                <SortHeader column="label" label="Who" params={params} />
                <th>Type</th>
                <th>Outcome</th>
                <SortHeader column="looked_at" label="Looked at" params={params} />
                <th className="num">Pages read</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {(members.data ?? []).map((m) => (
                <tr key={`${m.entity_type}-${m.entity_id}`}>
                  <td>{orAbsent(m.label)}</td>
                  <td className="muted">{orAbsent(m.entity_type)}</td>
                  <td>
                    {m.outcome === "unreachable"
                      ? <span className="refusal" title="No site answered.">could not reach</span>
                      : m.outcome === "no_result" ? "nothing found"
                      : m.outcome === "found" ? "found"
                      : <span className="muted">waiting</span>}
                  </td>
                  <td>{shortDate(m.looked_at)}</td>
                  <td className="num">{pagesRead(m.signals) || ""}</td>
                  <td>
                    {/* The point of the whole exercise. For a found record this
                        is the job the search actually turned up, which is what
                        tells somebody whether the credits were worth spending. */}
                    {(() => {
                      const jobs = jobsFound(m.employment);
                      const added = m.employment_since_lookup ?? 0;
                      if (!jobs.length) {
                        const say = m.outcome === "found"
                          ? nothingToShow(m.entity_type) : null;
                        return <span className="muted">{say}</span>;
                      }
                      return (
                        <>
                          {jobs.slice(0, 2).map((j, i) => (
                            <div key={i}>{jobLine(j)}</div>
                          ))}
                          {jobs.length > 2 ? (
                            <span className="muted">and {jobs.length - 2} more</span>
                          ) : null}
                          {added ? (
                            <div className="muted">{added} new since this search</div>
                          ) : null}
                        </>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ListShell>
      ) : null}
    </>
  );
}
